import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        // Fastri primary: yellow background with dark border
        default:
          'bg-primary text-primary-foreground border-2 border-foreground shadow-md hover:bg-primary/90 hover:shadow-lg active:scale-[0.98]',
        // Fastri brand: inverted - dark bg, yellow text, hover swaps
        brand:
          'bg-foreground text-primary border-2 border-foreground shadow-md hover:bg-primary hover:text-foreground active:scale-[0.98]',
        // Destructive: red for dangerous actions
        destructive:
          'bg-destructive text-destructive-foreground border-2 border-destructive shadow-sm hover:bg-destructive/90 active:scale-[0.98]',
        // Outline: dark border, white background (Fastri secondary style)
        outline:
          'border-2 border-foreground bg-background shadow-sm hover:bg-foreground hover:text-background active:scale-[0.98]',
        // Secondary: subtle gray background
        secondary:
          'bg-secondary text-secondary-foreground border-2 border-border shadow-sm hover:bg-secondary/80 active:scale-[0.98]',
        // Ghost: minimal, no border
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        // Link: text only
        link: 'text-primary underline-offset-4 hover:underline',
        // Success: green for positive actions
        success:
          'bg-green-500 text-white border-2 border-green-600 shadow-sm hover:bg-green-600 active:scale-[0.98]',
      },
      size: {
        default: 'h-10 px-4 py-2 [&_svg]:size-4',
        sm: 'h-9 rounded-lg px-3 text-xs [&_svg]:size-3.5',
        lg: 'h-12 rounded-xl px-8 text-base [&_svg]:size-5',
        xl: 'h-14 rounded-2xl px-10 text-lg [&_svg]:size-6',
        icon: 'h-10 w-10 [&_svg]:size-5',
        'icon-sm': 'h-8 w-8 rounded-lg [&_svg]:size-4',
        'icon-lg': 'h-12 w-12 [&_svg]:size-6',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
